import { AccountInfo } from '@efwwit/integration-hub-sdk/dist/nodejs/types/identity';
import { Assessment, BusinessCenter, Check, ContentCopy, DoDisturb, Flight, Lock, MoreVert, RecentActors, SearchSharp, Visibility } from '@mui/icons-material';
import LocalAirportIcon from '@mui/icons-material/LocalAirport';
import { Autocomplete, AutocompleteOption, Avatar, Box, Button, Checkbox, Chip, Divider, FormControl, FormLabel, Grid, IconButton, Input, LinearProgress, ListItemContent, ListItemDecorator, Radio, RadioGroup, TabList, TabPanel, Tabs, Textarea, Theme, Typography, chipClasses, useTheme } from '@mui/joy';
import Tab, { tabClasses } from "@mui/joy/Tab";
import { useEffect, useState } from 'react';
import { useHash } from 'react-use';
import { useGlobalContext } from '../../Context';
import { Core } from '../../utilities/core';
import { Profile } from '../../utilities/identity';
import AvatarCard from '../AccountProfile/AvatarCard';
import Header from '../Home/Header';
import { toggleSidebar } from '../utils';
import Comments from './comments';
import KickoffSelector from './kickoff';

const tabTheme = (theme: Theme) => {
    return {
        '--Chip-minHeight': '20px',
        '--ListItem-minHeight': '48px',
        top: 'calc(-1 * (var(--main-paddingTop, 0px) - var(--Header-height, 0px)))',
        zIndex: 10,
        width: '100%',
        overflow: 'auto hidden',
        alignSelf: 'flex-start',
        scrollSnapType: 'inline',
        '&::after': {
            pointerEvents: 'none',
            display: { xs: 'block', sm: 'none' },
            content: '""',
            position: 'sticky',
            top: 0,
            width: 40,
            flex: 'none',
            zIndex: 1,
            right: 0,
            borderBottom: '1px solid transparent',
            backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar': {
            width: 0,
            display: 'none',
        },
        [`& .${tabClasses.root}`]: {
            '--focus-outline-offset': '-2px',
            scrollSnapAlign: 'start',
            bgcolor: 'transparent',
            flex: 'none',
            '&:hover': {
                bgcolor: 'transparent',
            },
            [`&.${tabClasses.selected}`]: {
                color: 'primary.plainColor',
                bgcolor: 'transparent',
                [`& .${chipClasses.root}`]: theme.variants.solid.primary,
            },
        },
    }
}

interface EmergencyInfo {
    label: string
    office: string
    phone: string
}

interface NameInfo {
    firstName: string;
    lastName: string;
    middleName?: string;
    title?: string;
    dateOfBirth?: string;
}

export default function FlightMapper() {
    // eslint-disable-next-line
    const [hash, setHash] = useHash();
    const [profiles, setProfiles] = useState([] as AccountInfo[]);
    const [selectedUpn, setSelectedUpn] = useState<string>();
    const [selectedProfile, setSelectedProfile] = useState<AccountInfo>();
    const { core, rootUser } = useGlobalContext();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [ending, setEnding] = useState<string>("SB");
    const [gender, setGender] = useState<string>("");
    const [kickoff, setKickoff] = useState<string>("");
    const [reason, setReason] = useState<number>(5311);
    const [emergency, setEmergency] = useState<EmergencyInfo>();
    const [dob, setDOB] = useState<string>();

    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [middleName, setMiddleName] = useState<string>("");

    const [genderMarker, setGenderMarker] = useState<string>("");
    const [manager, setManager] = useState<boolean>(false);
    const [semicolon, setSemicolon] = useState<boolean>(true);

    const [editorType, setEditorType] = useState<"amadeus" | "sabre">("amadeus");
    const [editorText, setEditorText] = useState<string>("");

    const [textCopy, setTextCopy] = useState<boolean>(false);

    const [nameInfo, setNameInfo] = useState<NameInfo>();
    const [isHCM, setIsHCM] = useState<boolean>(false);
    const theme = useTheme();

    useEffect(() => {
        if (core && selectedUpn) {
            setIsLoading(true);
            setSelectedProfile(undefined);
            setIsHCM(false);
            setDOB(undefined);
            setMiddleName("");
            setGender("");
            setGenderMarker("");
            setKickoff("");
            setReason(5311);
            setEmergency({
                label: "BOS",
                office: "BOS",
                phone: "+1 877 638 5538"
            });
            setManager(false);
            setEditorType("amadeus");
            Profile.getUserByID(core, selectedUpn).then((profileResp) => {
                setSelectedProfile(profileResp[0]);
            });
        }
    }, [selectedUpn]);

    useEffect(() => {
        if (core && selectedProfile) {
            Profile.getNameData(core, (selectedProfile?.Basic?.integrationIdentifier) as string).then((hcmData) => {
                setNameInfo(hcmData.body as NameInfo);
            });
        }
    }, [selectedProfile]);

    useEffect(() => {
        if (nameInfo && nameInfo.dateOfBirth) {
            setIsHCM(true);
            setIsLoading(false);
            setDOB(nameInfo.dateOfBirth || "");
            setMiddleName(nameInfo.middleName || "");
            setGender(nameInfo?.title || "");
            setGenderMarker(getGenderFromTitle(nameInfo?.title || ""));
            setFirstName(nameInfo.firstName || "");
            setLastName(nameInfo.lastName || "");
        } else {
            setIsHCM(false);
            setIsLoading(false);
            if (selectedProfile) {
                setFirstName(selectedProfile.givenName);
                setLastName(selectedProfile.surname);
            }
        }

    }, [nameInfo]);

    useEffect(() => {
        setHash("#/travel/mapper");
    }, []);

    useEffect(() => {
        switch (editorType) {
            case "sabre":
                setEditorText(generateSabreText());
                break;
            default:
                setEditorText(generateAmadeusText());
        }
    }, [
        ending,
        gender,
        genderMarker,
        kickoff,
        reason,
        selectedProfile,
        emergency,
        manager,
        semicolon,
        dob,
        firstName,
        lastName,
        middleName,
        editorType
    ]);

    useEffect(() => {
        if (textCopy) {
            navigator.clipboard.writeText(editorText);
            setTimeout(() => {
                setTextCopy(false);
            }, 2000);
        }
    }, [textCopy])



    const uuidRegex = /(?:\{{0,1}(?:[0-9a-fA-F]){8}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){12}\}{0,1})/;

    const profileLookup = async (core: Core, name: string): Promise<AccountInfo[]> => {
        if (name.match(uuidRegex)) {
            const searchResult = await Profile.getUserByID(core, name);
            return searchResult;
        } else {
            const nameList = await Profile.queryLookup(core, name);
            return nameList;
        }
    }

    let timeout: NodeJS.Timeout;
    const handleProfileLookup = async (event: any) => {
        if (core) {
            const searchTerm = event.target ? (event.target as HTMLTextAreaElement).value : "";
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                if (searchTerm && searchTerm.length > 0) {
                    profileLookup(core, searchTerm).then((nameList) => {
                        setProfiles(nameList);
                    });
                } else {
                    setProfiles([]);
                }
            }, 500);
        }
    }

    const getGenderFromTitle = (title: string): string => {
        const cleanTitle = title.trim().toLowerCase().replace(".", "");
        const femaleTitles = new Set(["ms", "mrs", "miss"]);
        const maleTitles = new Set(["mr"]);

        if (maleTitles.has(cleanTitle)) {
            return "M";
        }
        if (femaleTitles.has(cleanTitle)) {
            return "F";
        }

        return title;
    };

    const formatPersonalPhone = (phone: string): string => {
        if (!phone) return "";

        // Remove all spaces
        let formatted = phone.replace(/\s/g, "");

        // If phone starts with +, replace it with 00
        if (formatted.startsWith("+")) {
            formatted = "00" + formatted.substring(1);
        }

        return formatted;
    };

    const generateAmadeusText = () => {
        if (selectedProfile) {
            const fullProfile = (selectedProfile as any as AccountInfo);

            let dobString = "";
            if (dob) {
                const [year, month, date] = dob.split("-");
                const paddedDate = date.padStart(2, '0');
                dobString = `${paddedDate}${Intl.DateTimeFormat("en", { "month": "short" }).format(new Date(month))}${year.toString().substring(2)}`.toUpperCase()
            }
            const dobField = `SR DOCS YY HK1-----${dobString}-${genderMarker}--${lastName.toUpperCase()}/${firstName.toUpperCase()}${middleName ? ` ${middleName.toUpperCase()}` : ""}/P1`;
            const lineEnding = semicolon ? ";" : "";

            const sixMonthsFromNow = new Date();
            sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

            return [
                `NM1${lastName.toUpperCase()}/${firstName.toUpperCase()}${middleName ? ` ${middleName.toUpperCase()}` : ""}${gender ? ` ${gender.toUpperCase()}` : ""}${lineEnding}`,
                dob ? `${dobField}${lineEnding}` : "",
                `APE-${fullProfile.mail.toUpperCase()}${lineEnding}`,
                manager ? `APE-${fullProfile.manager.mail.toUpperCase()}${lineEnding}` : "",
                `AP EF TRAVEL ${rootUser?.givenName.toUpperCase()} ${rootUser?.surname.toUpperCase()}${lineEnding}`,
                `AP STAFF TRAVEL +1-617-619-2636${lineEnding}`,
                `FM0${lineEnding}`,
                `FPCHECK${lineEnding}`,
                `RII EMERGENCY CONTACT STAFF TRAVEL ${emergency?.phone}${lineEnding}`,
                `RM*ACECRM-CA1-${reason}/CA2-${fullProfile.Finance?.product}/CA3-${fullProfile.Finance?.salesMarket}/CA4-${fullProfile.Finance?.productMarket}/CA5-${fullProfile.Finance?.costCenter}/CA6-${fullProfile.id}/CA11-${fullProfile.id}${lineEnding}`,
                `RM*ACECRM-SA1-${reason}/SA2-${fullProfile.Finance?.product}/SA3-${fullProfile.Finance?.salesMarket}/SA4-${fullProfile.Finance?.productMarket}/SA5-${fullProfile.Finance?.costCenter}/SA6-${fullProfile.id}/SA11-${fullProfile.id}${lineEnding}`,
                `RM*ACECLN-${fullProfile.Finance?.company}${ending}${lineEnding}`,
                `RM*ACECRM-AMM-STAFF${lineEnding}`,
                `RM ACCOUNTING-/CA1-${reason}/CA2-${fullProfile.Finance?.product}/CA3-${fullProfile.Finance?.salesMarket}/CA4-${fullProfile.Finance?.productMarket}/CA5-${fullProfile.Finance?.costCenter}/CA11-${fullProfile.id}/P1${lineEnding}`,
                `RM BILLING-${fullProfile.Finance?.company}${ending}/P1${lineEnding}`,

                kickoff ? `RM*ACECRM-KO-${kickoff}${lineEnding}` : "",
                kickoff ? `RM*ACECRM-CA7-${kickoff}${lineEnding}` : "",
                kickoff ? `RM*ACECRM-SA7-${kickoff}${lineEnding}` : "",
                kickoff ? `RM ACCOUNTING-/CA10-${kickoff}/P1${lineEnding}` : "",

                `SR*CTCM-${formatPersonalPhone(selectedProfile?.Protected?.personalPhone || "")}${lineEnding}`,
                `SR*CTCE- ${fullProfile.mail.toUpperCase().split("@").join("//")}${lineEnding}`,
                `RU1AHK1${emergency?.office}${sixMonthsFromNow.getDate()}${sixMonthsFromNow.toLocaleString("default", { month: "short" }).toUpperCase()}/HOLDPNR${lineEnding}`
            ].filter(r => r).join("\n");
        } else {
            return "";
        }
    }

    const generateSabreText = () => {
        if (selectedProfile) {
            const fullProfile = (selectedProfile as any as AccountInfo);

            let dobString = "";
            if (dob) {
                const dobDate = new Date(dob);
                const paddedDate = dobDate.getDate().toString().padStart(2, '0');
                dobString = `${paddedDate}${dobDate.toLocaleString('default', { month: 'short' }).toUpperCase()}${dobDate.getFullYear().toString().substring(2)}`.toUpperCase();
            }
            const dobField = `3DOCS/DB/${dobString}/${genderMarker}/${lastName.toUpperCase()}/${firstName.toUpperCase()}${middleName ? ` ${middleName.toUpperCase()}` : ""}-1.1`;

            return [
                `-${lastName.toUpperCase()}/${firstName.toUpperCase()}${middleName ? ` ${middleName.toUpperCase()}` : ""} ${gender.toUpperCase()}§`,
                `PE¥${fullProfile.mail.toUpperCase()}¥§`,
                manager ? `PE¥${fullProfile.manager.mail.toUpperCase()}¥§` : "",
                dob ? `${dobField}` : "",
                `5.ACECRM-CA1-${reason}/CA2-${fullProfile.Finance?.product}/CA3-${fullProfile.Finance?.salesMarket}/CA4-${fullProfile.Finance?.productMarket}/CA5-${fullProfile.Finance?.costCenter}§`,
                `5.ACECRM-CA6-${fullProfile.id}§`,
                `5.ACECRM-CA11-${fullProfile.id}§`,
                `5.ACECRM-SA1-${reason}/SA2-${fullProfile.Finance?.product}/SA3-${fullProfile.Finance?.salesMarket}/SA4-${fullProfile.Finance?.productMarket}/SA5-${fullProfile.Finance?.costCenter}§`,
                `5.ACECRM-SA6-${fullProfile.id}§`,
                `5.ACECRM-SA11-${fullProfile.id}§`,
                `5.ACECLN-${fullProfile.Finance?.company}${ending}§`,
                `5.ACCOUNTING-CA1-${reason}/P1§`,
                `5.ACCOUNTING-CA2-${fullProfile.Finance?.product}/P1§`,
                `5.ACCOUNTING-CA3-${fullProfile.Finance?.salesMarket}/P1§`,
                `5.ACCOUNTING-CA4-${fullProfile.Finance?.productMarket}/P1§`,
                `5.ACCOUNTING-CA5-${fullProfile.Finance?.costCenter}/P1§`,
                `5.ACCOUNTING-CA11-${fullProfile.id}/P1§`,
                `5.BILLING-${fullProfile.Finance?.company}${ending}/P1§`,

                kickoff ? `5.ACECRM-KO-${kickoff}§` : "",
                kickoff ? `5.ACECRM-CA7-${kickoff}§` : "",
                kickoff ? `5.ACECRM-SA7-${kickoff}§` : "",
                kickoff ? `5.ACCOUNTING-CA10-${kickoff}/P1§` : "",

                `3CTCM/${formatPersonalPhone(selectedProfile?.Protected?.personalPhone || "")}-1.1`,
                `3CTCE/${fullProfile.mail.toUpperCase().split("@").join("//")}−1.1`,
            ].filter(r => r).join("\n");
        }

        return "";

    }

    return (
        (((rootUser?.RBAC?.iaaRole || []) as any).includes("staffTravel") || (rootUser?.RBAC?.iaaRole || []).includes('globalAdmin')) ?
            <Box
                sx={{
                    flex: 1,
                    pl: 2,
                    pr: 2,
                    pt: 0,
                    maxWidth: 1200,
                    width: '100%',
                    mx: 'auto',
                }}>
                <Header icon={<RecentActors />} title={'Staff Travel'}></Header>
                <Typography mb={2}>
                    This section provides utilities for staff travel to obtain and manage information on staff accounts across EF.
                </Typography>
                <IconButton
                    sx={{
                        display: {
                            xs: "block",
                            sm: "block",
                            md: "none",
                            lg: "none",
                        },
                        position: "absolute",
                        top: 0,
                        right: 0,
                        pt: 0.5,
                        m: 2,
                        zIndex: 5
                    }}
                    onClick={() => toggleSidebar()}
                    color="neutral"
                >
                    <MoreVert sx={{ color: "white" }} />
                </IconButton>
                <Tabs
                    value={0}
                    sx={{ bgcolor: 'transparent' }}>
                    <TabList
                        //#region Tab Theme
                        sticky="top"
                        variant="plain"
                        sx={tabTheme}
                    //#endregion
                    >
                        <Tab value={0} key={0}>
                            <Box>Flight Mapper <sup style={{ color: "#bf4526" }}>Beta</sup></Box>
                        </Tab>
                    </TabList>
                    <Box
                        //#region Container Theme
                        sx={{
                            height: 0,
                            position: 'sticky',
                            top: 'calc(48px - var(--main-paddingTop, 0px) + var(--Header-height, 0px) - (var(--_shadow-height) / 2))',
                            zIndex: 1,
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'relative',
                                zIndex: 1,
                                height: 'var(--_shadow-height)',
                            },
                        }}
                    //#endregion
                    >
                        <TabPanel
                            sx={{
                                padding: 0
                            }}
                            key={0}
                            value={0}>
                            <Box sx={{
                                display: { xs: 'grid', sm: 'grid' },
                                flexWrap: "wrap",
                                gap: 2,
                                pb: 3
                            }}>
                                <Autocomplete
                                    sx={{
                                        mt: 3
                                    }}
                                    placeholder="Search here..."
                                    variant="soft"
                                    size="lg"
                                    endDecorator={<SearchSharp />}
                                    options={profiles}
                                    getOptionLabel={option => option.displayName}
                                    onInputChange={handleProfileLookup}
                                    onChange={(evt, val, reason) => {
                                        if (reason === "selectOption") {
                                            setSelectedUpn((val as any).id);
                                        }
                                    }}
                                    isOptionEqualToValue={() => {
                                        return true;
                                    }}
                                    filterOptions={(x) => x} //disables built-in filtering
                                    renderOption={(props, option) => (
                                        <AutocompleteOption {...props}>
                                            <ListItemDecorator>
                                                <Avatar />
                                            </ListItemDecorator>
                                            <ListItemContent sx={{ fontSize: 'sm' }}>
                                                {option.displayName}
                                                <Typography level="body-xs">
                                                    {option.userPrincipalName}
                                                </Typography>
                                            </ListItemContent>
                                        </AutocompleteOption>
                                    )}
                                />
                            </Box>
                            {
                                isLoading &&
                                <LinearProgress />
                            }
                            {
                                (selectedProfile && nameInfo) &&
                                <Box sx={{
                                    display: { xs: 'grid', sm: 'grid' },
                                    flexWrap: "wrap",
                                    gap: 2,
                                    pb: 3
                                }}>
                                    <Divider role="presentation" sx={{ gridColumn: "1/-1" }}>
                                        <Chip size='lg' startDecorator={<Visibility />}>
                                            Overview
                                        </Chip>
                                    </Divider>
                                    <AvatarCard profile={selectedProfile as any as AccountInfo}></AvatarCard>
                                    <Divider role="presentation" sx={{ gridColumn: "1/-1" }}>
                                        <Chip size='lg' startDecorator={<RecentActors />}>
                                            Personal Information
                                        </Chip>
                                    </Divider>
                                    <Grid mt={2} container
                                        spacing={{ xs: 2, md: 2 }}
                                        columns={{ xs: 4, sm: 6, md: 12 }}
                                        sx={{ flexGrow: 1 }}
                                    >
                                        <Grid xs={6} md={6} key={0}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Title"}</FormLabel>
                                                {
                                                    (isHCM) ?
                                                        <Input
                                                            disabled={true}
                                                            endDecorator={<Lock sx={theme.palette.mode === "dark" ? { color: "#CFD8DC" } : { color: "#636b74" }} />}
                                                            startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Title"}</Chip>}
                                                            value={nameInfo.title}
                                                        ></Input>
                                                        :
                                                        <Autocomplete
                                                            disabled={false}
                                                            startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Title"}</Chip>}
                                                            options={[
                                                                "Mr", "Ms", "Mrs"
                                                            ]}
                                                            defaultValue={gender || ""}
                                                            onChange={(evt, val) => {
                                                                if (val) {
                                                                    setGender(val);
                                                                    setGenderMarker(getGenderFromTitle(val));
                                                                }
                                                            }}
                                                        ></Autocomplete>
                                                }

                                            </FormControl>
                                        </Grid>
                                        <Grid xs={6} md={6} key={1}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"First Name"}</FormLabel>
                                                <Input
                                                    disabled={true}
                                                    endDecorator={<Lock sx={theme.palette.mode === "dark" ? { color: "#CFD8DC" } : { color: "#636b74" }} />}
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"First Name"}</Chip>}
                                                    value={firstName}
                                                    onInput={e => {
                                                        if ((e.target as any).value.length > 0) {
                                                            setFirstName((e.target as any).value);
                                                        } else {
                                                            setFirstName(firstName);
                                                        }
                                                    }}
                                                ></Input>
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={6} md={6} key={3}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Middle Name"}</FormLabel>
                                                <Input
                                                    disabled={isHCM}
                                                    endDecorator={isHCM ? <Lock sx={theme.palette.mode === "dark" ? { color: "#CFD8DC" } : { color: "#636b74" }} /> : undefined}
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Middle Name"}</Chip>}
                                                    defaultValue={middleName || ""}
                                                    value={middleName || ""}
                                                    onInput={e => {
                                                        if ((e.target as any).value.length > 0) {
                                                            setMiddleName((e.target as any).value);
                                                        } else {
                                                            setMiddleName("");
                                                        }
                                                    }}
                                                ></Input>
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={6} md={6} key={2}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Last Name"}</FormLabel>
                                                <Input
                                                    disabled={true}
                                                    endDecorator={<Lock sx={theme.palette.mode === "dark" ? { color: "#CFD8DC" } : { color: "#636b74" }} />}
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Last Name"}</Chip>}
                                                    value={lastName}
                                                    onInput={e => {
                                                        if ((e.target as any).value.length > 0) {
                                                            setLastName((e.target as any).value);
                                                        } else {
                                                            setLastName(selectedProfile.surname);
                                                        }
                                                    }}
                                                ></Input>
                                            </FormControl>
                                        </Grid>



                                        <Grid xs={6} md={6} key={8}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Date of Birth"}</FormLabel>
                                                <Input
                                                    disabled={isHCM ? true : false}
                                                    endDecorator={isHCM ? <Lock sx={theme.palette.mode === "dark" ? { color: "#CFD8DC" } : { color: "#636b74" }} /> : undefined}
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Date of Birth"}</Chip>}
                                                    type='date'
                                                    value={dob || ""}
                                                    onInput={e => setDOB((e.target as any).value)}
                                                >
                                                </Input>
                                            </FormControl>
                                        </Grid>
                                        <Grid xs={6} md={6} key={10}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Gender"}</FormLabel>
                                                <Autocomplete
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Gender"}</Chip>}
                                                    options={["M", "F"]}
                                                    value={genderMarker || null}
                                                    onChange={(evt, val) => {
                                                        setGenderMarker(val || "");
                                                    }}
                                                ></Autocomplete>
                                            </FormControl>
                                        </Grid>
                                        <Grid xs={12}>
                                            <Divider role="presentation" sx={{ my: 2 }}>
                                                <Chip size='lg' startDecorator={<LocalAirportIcon />}>Travel Information</Chip>
                                            </Divider>
                                        </Grid>
                                        <Grid xs={6} md={6} key={4}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Trip Reason"}</FormLabel>
                                                <Autocomplete
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Trip Reason"}</Chip>}
                                                    defaultValue={{
                                                        label: "Business - Staff",
                                                        value: 5311
                                                    }}
                                                    onChange={(evt, val) => {
                                                        if (val) setReason(val.code || 5311);
                                                    }}

                                                    renderOption={(props, option) => (
                                                        <AutocompleteOption {...props}>
                                                            <ListItemDecorator>
                                                                <Flight />
                                                            </ListItemDecorator>
                                                            <ListItemContent sx={{ fontSize: 'sm' }}>
                                                                {option.label}
                                                                <Typography level="body-xs">
                                                                    {option.code}
                                                                </Typography>
                                                            </ListItemContent>
                                                        </AutocompleteOption>
                                                    )}
                                                    options={[
                                                        {
                                                            label: "Agent incentive",
                                                            code: 7821
                                                        },
                                                        {
                                                            label: "Business - Staff",
                                                            code: 5311
                                                        },
                                                        {
                                                            label: "External Consultant",
                                                            code: 6599
                                                        },
                                                        {
                                                            label: "Recruitment - Field Staff",
                                                            code: 4396
                                                        },
                                                        {
                                                            label: "Recruitment - Staff",
                                                            code: 5913
                                                        },
                                                        {
                                                            label: "Relocation",
                                                            code: 5912
                                                        }
                                                    ]}
                                                ></Autocomplete>
                                            </FormControl>
                                        </Grid>
                                        <Grid xs={6} md={6} key={5}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Ending"}</FormLabel>
                                                <Autocomplete
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Ending"}</Chip>}
                                                    defaultValue={"SB"}
                                                    onChange={(evt, val) => {
                                                        if (val) setEnding(val);
                                                    }}
                                                    options={[
                                                        "SB", "SP", "TR"
                                                    ]}
                                                ></Autocomplete>
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={6} md={6} key={6}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Remark"}</FormLabel>
                                                <KickoffSelector kickoff={kickoff} setKickoff={setKickoff} />
                                            </FormControl>
                                        </Grid>

                                        <Grid xs={6} md={6} key={7}>
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                                <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Session and Emergency office"}</FormLabel>
                                                <Autocomplete
                                                    startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Session and Emergency office"}</Chip>}
                                                    defaultValue={{
                                                        label: "BOS",
                                                        office: "BOS",
                                                        phone: "+1 877 638 5538"
                                                    }}
                                                    renderOption={(props, option) => (
                                                        <AutocompleteOption {...props}>
                                                            <ListItemDecorator>
                                                                <BusinessCenter />
                                                            </ListItemDecorator>
                                                            <ListItemContent sx={{ fontSize: 'sm' }}>
                                                                {option.label}
                                                                <Typography level="body-xs">
                                                                    {option.office} ({option.phone})
                                                                </Typography>
                                                            </ListItemContent>
                                                        </AutocompleteOption>
                                                    )}
                                                    onChange={(evt, val) => {
                                                        if (val) setEmergency(val);
                                                    }}
                                                    options={[
                                                        {
                                                            label: "BOS",
                                                            office: "BOS",
                                                            phone: "+1 877 638 5538"
                                                        },
                                                        {
                                                            label: "QLJ",
                                                            office: "QLJ",
                                                            phone: "+41 079 881 5031"
                                                        },
                                                        {
                                                            label: "YYZ",
                                                            office: "YYZ",
                                                            phone: "+1 877 638 5538"
                                                        },
                                                        {
                                                            label: "HKG",
                                                            office: "HKG",
                                                            phone: "+852 5726 8110"
                                                        },
                                                        {
                                                            label: "ZRH",
                                                            office: "ZRH",
                                                            phone: "+41 079 881 5031"
                                                        },
                                                        {
                                                            label: "SHA",
                                                            office: "SHA",
                                                            phone: "+86 136 7169 1940"
                                                        }
                                                    ]}
                                                ></Autocomplete>
                                            </FormControl>
                                        </Grid>


                                        <Grid xs={6} md={6} key={9}>
                                            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' }, display: "inline-block" }}>
                                                <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
                                                    <Checkbox checked={manager} label="Manager" onChange={() => setManager(!manager)} />
                                                    <Checkbox checked={semicolon} label="Semicolon" onChange={() => setSemicolon(!semicolon)} defaultChecked />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Comments key={11} profile={selectedProfile} />

                                    <Divider role="presentation" sx={{ gridColumn: "1/-1" }}>
                                        <Chip size='lg' startDecorator={<Assessment />}>
                                            Output
                                        </Chip>
                                    </Divider>
                                    <Grid key={12} container direction="row" sx={{ justifyContent: "space-between" }}>
                                        <Grid >
                                            <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' }, display: "inline-block" }}>
                                                <RadioGroup onChange={(e) => setEditorType(e.target.value as "amadeus" | "sabre")} orientation="horizontal" defaultValue="amadeus" name="radio-buttons-group">
                                                    <Radio value="amadeus" label="Amadeus" />
                                                    <Radio value="sabre" label="Sabre" />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                        <Grid>
                                            <Button onClick={() => setTextCopy(true)} startDecorator={textCopy ? <Check /> : <ContentCopy />}>{textCopy ? "Copied!" : "Copy"}</Button>
                                        </Grid>
                                    </Grid>
                                    <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: '49%' } }}>
                                        <FormLabel sx={{ display: { sm: 'none', xs: 'flex' } }}>{"Output"}</FormLabel>
                                        <Textarea
                                            // readOnly
                                            minRows={8}
                                            value={editorText}
                                            onChange={e => setEditorText(e.target.value || "")}
                                            startDecorator={<Chip sx={{ display: { sm: 'flex', xs: 'none' } }}>{"Output"}</Chip>}
                                        />
                                    </FormControl>
                                </Box>
                            }
                        </TabPanel>
                    </Box>
                </Tabs >
            </Box > :
            <Box
                sx={{
                    flex: 1,
                    pl: 2,
                    pr: 2,
                    pt: 0,
                    maxWidth: 1200,
                    width: '100%',
                    mx: 'auto',
                }}>
                <Header icon={<DoDisturb />} title={'Access Denied'}></Header>
                <Typography mb={2}>
                    We are sorry, but you do not have access to this resource.
                </Typography>
                <IconButton
                    sx={{
                        display: {
                            xs: "block",
                            sm: "block",
                            md: "none",
                            lg: "none",
                        },
                        position: "absolute",
                        top: 0,
                        right: 0,
                        pt: 0.5,
                        m: 2,
                        zIndex: 5
                    }}
                    onClick={() => toggleSidebar()}
                    color="neutral"
                >
                    <MoreVert sx={{ color: "white" }} />
                </IconButton>

            </Box>
    )
}